/*
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { Switch } from '@/utils/switch';
import { Box, Button, ButtonBase, ButtonProps, CircularProgress, IconButton } from '@mui/material';
import { FC, MouseEvent, useState } from 'react';

type Props = ButtonProps & {
	spin?: boolean;
	spinSize?: number;
	wrapper?: 'button' | 'icon' | 'button-base' | 'button-link' | 'dp-button';
	children?: any;
	isOutOfOtock?: boolean;
};

export const OneClick: FC<Props> = ({
	onClick,
	disabled,
	children,
	spin,
	wrapper,
	spinSize,
	isOutOfOtock,
	...props
}) => {
	const [disabledOverride, setDisabledOverride] = useState<boolean>(false);

	const onClickOverride = async (event: MouseEvent<HTMLButtonElement>) => {
		if (onClick) {
			try {
				setDisabledOverride(true);
				await onClick(event);
			} finally {
				setDisabledOverride(false);
			}
		}
	};

	return disabledOverride && spin ? (
		<Box component="span">
			<CircularProgress size={spinSize ?? 40} />
		</Box>
	) : (
		Switch(wrapper)
			.case('icon', () => (
				<IconButton
					{...{
						...props,
						disabled: disabledOverride || disabled,
						onClick: onClickOverride,
					}}
				>
					{children}
				</IconButton>
			))
			.case('button-base', () => (
				<ButtonBase
					{...{ ...props, disabled: disabledOverride || disabled, onClick: onClickOverride }}
				>
					{children}
				</ButtonBase>
			))
			.case('dp-button', () => (
				<DpButton
					{...({
						...props,
						disabled: disabledOverride || disabled,
						onClick: onClickOverride,
					} as any)}
				>
					{children}
				</DpButton>
			))
			.case('button-link', () => (
				<ButtonBase
					{...{ ...props, disabled: disabledOverride || disabled, onClick: onClickOverride }}
					sx={{
						padding: '0px',
						color: 'primary.main',
						fontSize: 'var(--font-size-body)',
						fontWeight: 600,
						'& p': {
							fontSize: 'var(--font-size-body)',
							lineHeight: 0,
							fontWeight: 600,
						},
					}}
				>
					{(children?.props?.children as any) === 'Editado' ? 'Editar' : children ?? ''}
				</ButtonBase>
			))
			.defaultTo(() => (
				<Button
					{...{ ...props, disabled: disabledOverride || disabled, onClick: onClickOverride }}
					sx={{
						width: '100%',
						backgroundColor: isOutOfOtock ? '#727272 ' : '#FF4900',
						color: '#FFFFFF',
						fontSize: 'var(--font-size-body)',
						letterSpacing: '0',
						fontWeight: 'bold',
						borderRadius: '22px',
						height: '44px',
						'&:hover': { backgroundColor: '#d83f01' },
						marginTop: isOutOfOtock ? '-11px' : '0px',
					}}
				>
					{children}
				</Button>
			))
	);
};
